import { VisionGpuState } from "@sitescene/types";

import { Button } from "@/components/ui/buttons/button";
import { useVisionGpu, useWarmGpu } from "@/features/vision-lab/use-vision-lab";
import { cx } from "@/utils/cx";

const LABELS: Record<VisionGpuState, string> = {
	[VisionGpuState.NotDeployed]: "No GPU on this stage",
	[VisionGpuState.Off]: "GPU off",
	[VisionGpuState.Ready]: "GPU ready",
	[VisionGpuState.Starting]: "GPU starting (about 5 min from cold)",
};

const DOTS: Record<VisionGpuState, string> = {
	[VisionGpuState.NotDeployed]: "bg-gray-400",
	[VisionGpuState.Off]: "bg-gray-400",
	[VisionGpuState.Ready]: "bg-success-500",
	[VisionGpuState.Starting]: "bg-warning-500 animate-pulse",
};

/**
 * The GPU worker's state, and a button to start it while the video and
 * instruction are being picked. It scales back down after an idle hour.
 */
export const GpuChip = () => {
	const gpu = useVisionGpu();
	const warm = useWarmGpu();
	const state = gpu.data?.state;

	return (
		<div className="flex flex-wrap items-center gap-3 rounded-md border border-secondary px-3 py-2 text-sm">
			<span className="flex items-center gap-2">
				<span
					aria-hidden="true"
					className={cx(
						"size-2.5 rounded-full",
						state ? DOTS[state] : "bg-gray-300",
					)}
				/>
				{state
					? LABELS[state]
					: gpu.isLoading
						? "Checking the GPU…"
						: "GPU status unknown"}
			</span>
			{state === VisionGpuState.Off && (
				<Button
					color="secondary"
					isDisabled={warm.isPending}
					onClick={() => warm.mutate()}
					size="xs"
				>
					Start GPU
				</Button>
			)}
			{(gpu.error ?? warm.error) && (
				<span className="text-error-primary">
					{(gpu.error ?? warm.error)?.message}
				</span>
			)}
		</div>
	);
};
